import { useCallback, useLayoutEffect, useRef, useState } from 'react';

// The element's ancestors that can actually show a scrollbar, plus the page
// itself — the ones whose leftover overflow is worth trimming.
const scrollableAncestors = (element: HTMLElement): HTMLElement[] => {
	const found: HTMLElement[] = [document.documentElement];

	for (
		let node = element.parentElement;
		node && node !== document.documentElement;
		node = node.parentElement
	) {
		if (['auto', 'scroll'].includes(window.getComputedStyle(node).overflowY)) {
			found.push(node);
		}
	}

	return found;
};

// Sizes an element to whatever vertical space the page has left over, so it
// fills the viewport exactly and the page itself never scrolls. Returns a `ref`
// to attach to that element, the measured `height` to render it with, and
// `measure` for re-running it after something else on the page has resized.
//
// The default layout is content-sized the whole way down — `main` and
// `LayoutContent` are `md:h-fit`, and `LayoutWrapper`'s `flex-1` sits in a
// parent that is not a flex container — so `h-full` on a child resolves against
// no definite height and collapses to nothing. The alternative, hard-coding the
// surrounding chrome as a `calc(100dvh - …)`, has to guess at the page header,
// the wrapper margin and `LayoutContent`'s padding, and drifts by a few pixels
// the moment any of them change. So it is measured instead.
export const useFillAvailableHeight = (minHeight: number) => {
	const ref = useRef<HTMLDivElement>(null);
	const [height, setHeight] = useState(minHeight);

	const measure = useCallback(() => {
		const element = ref.current;

		if (!element) {
			return;
		}

		// Grow past the viewport before measuring. Two containers carry a
		// min-height — `min-h-screen` on the page wrapper and `min-h-[400px]` on
		// `LayoutContent` — and while the page is shorter than those, they prop it
		// open and `scrollHeight` reports their height rather than the real
		// content height, so the chrome below would measure far too large.
		element.style.height = `${window.innerHeight}px`;

		// Document coordinates throughout, so a scrolled page measures the same as
		// one at the top. What is measured is how much the layout renders *below*
		// the element, which does not change as the element grows.
		const rect = element.getBoundingClientRect();
		const top = rect.top + window.scrollY;
		const below =
			document.documentElement.scrollHeight - (rect.bottom + window.scrollY);

		const fitted = Math.floor(window.innerHeight - top - below);
		element.style.height = `${Math.max(fitted, minHeight)}px`;

		// Several ancestors compute to fractional heights (851.333px and the
		// like), which can leave a few pixels of overflow behind; trim whatever is
		// actually left rather than trying to predict the rounding. The page is
		// not the only thing that can scroll — `main` is `overflow-y-auto` — so
		// every scrollable ancestor is checked, not just the document.
		const residual = scrollableAncestors(element).reduce(
			(worst, ancestor) =>
				Math.max(worst, ancestor.scrollHeight - ancestor.clientHeight),
			0,
		);
		const next = Math.max(fitted - residual, minHeight);

		// Applied here rather than left to the re-render: when the measurement
		// matches what is already in state React has nothing to update, and a
		// stale height would stick.
		element.style.height = `${next}px`;
		setHeight(next);
	}, [minHeight]);

	useLayoutEffect(() => {
		measure();

		window.addEventListener('resize', measure);

		return () => window.removeEventListener('resize', measure);
	}, [measure]);

	return { ref, height, measure };
};
